import { useMemo } from "react";
import useCatalogEnabled from "catalog/hooks/useCatalogEnabled";
import useFilterParams from "catalog/hooks/useFilterParams";
import { buildFilterMatrix, HIDDEN_ENTITIES_TAG, INDICES } from "catalog/utils/buildFilterMatrix";
import queryString from "query-string";
import { SWRResponse } from "swr";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import SearchResponse, { TableEntity } from "~/types/interfaces/catalogResponse";
import { V1TableListEnvelope, V1TableSearchResult } from "~/types/interfaces/catalogTables";

export const PAGE_SIZE = 20;
const { VITE_CATALOG_API } = import.meta.env;

// The v1 /tables/search envelope, plus faceted-search aggregations.
type V1TableSearchResponse = V1TableListEnvelope<V1TableSearchResult> & {
  aggregations?: SearchResponse["aggregations"];
};

// Adapt the v1 /tables/search envelope into the OpenSearch-shaped SearchResponse the
// catalog table views already consume, so switching endpoints leaves those components
// (and the column search path) untouched. Only the fields those views read are
// populated, so the synthesized `_source` is a partial TableEntity.
const adaptTableSearch = (resp: V1TableSearchResponse): SearchResponse<TableEntity> => ({
  aggregations: resp.aggregations ?? {},
  hits: {
    total: { value: resp.metadata.total_count },
    hits: resp.items.map((item) => ({
      _score: item.score ?? 0,
      highlight: item.highlight,
      _source: {
        entityType: "table",
        id: item.fqn,
        name: item.name,
        description: item.description ?? null,
        fullyQualifiedName: item.fqn,
        tableType: item.table_type,
        databaseSchema: { name: item.database_schema },
        tags: (item.tags ?? []).map((tag) => ({
          tagFQN: tag.tag_fqn,
          description: tag.description,
        })),
      } as unknown as TableEntity,
    })),
  },
});

const useCatalogSearch = <T>(entityType: "table" | "column") => {
  const catalogEnabled = useCatalogEnabled();
  const [params] = useFilterParams();
  const { q: search, page, ttags = [], ctags = [] } = params;
  const debouncedSearch = useDebounce(search);
  const from = page ? (page - 1) * PAGE_SIZE : 0;
  const isTable = entityType === "table";

  // Column search (unchanged): the unversioned /search/query endpoint with an
  // OpenSearch filter matrix, which also excludes `ux.hidden` entities.
  const columnFilters = buildFilterMatrix(ctags as string[]);
  const columnQs = queryString.stringify({
    index: INDICES.column,
    q: debouncedSearch,
    query_filter: JSON.stringify(columnFilters),
    from,
    size: PAGE_SIZE,
  });

  // Table search: the v1 /tables/search endpoint. Selected tag facets map to repeated
  // `tag` params (the endpoint groups them by classification); an empty query is "*".
  // `ux.hidden` tables are excluded to match the column search filter matrix.
  const tableQs = queryString.stringify({
    query_term: debouncedSearch || "*",
    tag: ttags as string[],
    exclude_tag: HIDDEN_ENTITIES_TAG,
    offset: from,
    limit: PAGE_SIZE,
  });

  const key = catalogEnabled
    ? isTable
      ? `${VITE_CATALOG_API}/v1/tables/search?${tableQs}`
      : `${VITE_CATALOG_API}/search/query?${columnQs}`
    : null;

  const resp = useFetcher<SearchResponse<T> | V1TableSearchResponse>(key, {
    keepPreviousData: true,
  });

  const data = useMemo(
    () =>
      isTable && resp.data
        ? adaptTableSearch(resp.data as V1TableSearchResponse)
        : (resp.data as SearchResponse<T> | undefined),
    [isTable, resp.data]
  );

  return { ...resp, data } as SWRResponse<SearchResponse<T>>;
};

export default useCatalogSearch;
